import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as appStorage from '../utils/appIndexedDB';
import { useAppConfig } from '../context/AppConfigContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import SafeHTMLRenderer from './SafeHTMLRenderer';
import ChatChart from './ChatChart';
import A2UIReplySurface from './A2UIReplySurface';
import A2uiRenderErrorBoundary from './A2uiRenderErrorBoundary';
import McpAppFrame from './McpAppFrame';
import apiService from '../services/apiService';
import { mcpAppTitle } from '../utils/mcpApps';
import { parseA2uiV08MessagesFromPayload } from '../utils/parseA2uiV08MessagesFromPayload';
import { normalizeA2uiV08Messages } from '../utils/normalizeA2uiV08Messages';
import { hasA2uiV08ComponentCycle } from '../utils/hasA2uiV08ComponentCycle';
import { isA2uiV08BotReplyPayload } from '../utils/isA2uiV08BotReplyPayload';
import { formatMessageTime } from '../utils/formatMessageTime';
import 'katex/dist/katex.min.css';

// 圖片下載 Promise cache：同一 URL 在整個 session 只發一次 HTTP request。
// null 代表已確認失敗（404 / 網路錯誤），後續直接略過不重試。
// React StrictMode 雙重掛載時，第二個 instance 直接 await 同一個 Promise，不產生額外 request。
const _imagePromises = new Map();

function fetchImageBlobCached(serverUrl) {
  if (_imagePromises.has(serverUrl)) return _imagePromises.get(serverUrl);
  const token = appStorage.getItemSync('mygenie_token');
  const promise = token
    ? fetch(serverUrl, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => (res.ok ? res.blob() : null))
        .catch(() => null)
    : Promise.resolve(null);
  _imagePromises.set(serverUrl, promise);
  return promise;
}

function MessageTime({ message }) {
  const formatted = formatMessageTime(message);
  if (!formatted) return null;
  return (
    <time className="message-time" dateTime={formatted.dateTime}>
      {formatted.label}
    </time>
  );
}

// 檢測並解析圖表數據格式
const parseChartData = (text) => {
  try {
    // 嘗試解析JSON格式的圖表數據
    const jsonMatch = text.match(/\[[\s\S]*?\]/);
    if (jsonMatch) {
      const jsonStr = jsonMatch[0];
      const chartData = JSON.parse(jsonStr);
      
      // 驗證數據格式是否符合要求
      if (Array.isArray(chartData) && 
          chartData.every(item => 
            typeof item === 'object' && 
            'label' in item && 
            'value' in item &&
            typeof item.label === 'string' &&
            typeof item.value === 'number'
          )) {
        console.log('📊 檢測到圖表數據:', chartData);
        return chartData;
      }
    }
  } catch (error) {
    // console.log('❌ JSON解析失敗:', error.message);
  }
  return null;
};

/**
 * 按讚／倒讚：立即寫入 DB 並滑出留言區；按送出時更新留言（可為空字串）。
 * onFeedback(userFeedback, userComment?) — userComment 省略表示不變更留言欄位
 */
function BotMessageFeedback({ userFeedback, userComment, onFeedback, disabled, quickOptions = [] }) {
  const savedChoice = userFeedback === true ? 'up' : userFeedback === false ? 'down' : null;
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelRating, setPanelRating] = useState(null);
  const [comment, setComment] = useState(userComment || '');
  const [selectedOptions, setSelectedOptions] = useState(new Set());
  const [ratingLoading, setRatingLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const inFlightRef = useRef(false);
  const thankYouTimerRef = useRef(null);
  const feedbackScrollAnchorRef = useRef(null);
  const feedbackPanelRef = useRef(null);

  useEffect(() => {
    setComment(userComment || '');
  }, [userComment]);

  useEffect(() => {
    if (!panelOpen) setSelectedOptions(new Set());
  }, [panelOpen]);

  // 展開回饋面板後，僅在面板底部會被捲動容器遮住時才捲動；並隨面板展開動畫同步捲動，
  // 呈現「往下滾動展開」的效果，而非等動畫結束後才突然跳到底部。
  useEffect(() => {
    if (!panelOpen) return;
    const anchor = feedbackScrollAnchorRef.current;
    const panelEl = feedbackPanelRef.current;
    if (!anchor || !panelEl) return;
    const scrollParent = anchor.closest('.messages');
    if (!scrollParent) return;

    const parentRect = scrollParent.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const fullPanelHeight = panelEl.scrollHeight;
    const predictedBottom = anchorRect.bottom + fullPanelHeight;
    if (predictedBottom <= parentRect.bottom) return;

    let lastHeight = panelEl.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      const currentHeight = panelEl.getBoundingClientRect().height;
      const delta = currentHeight - lastHeight;
      if (delta !== 0) {
        scrollParent.scrollTop += delta;
        lastHeight = currentHeight;
      }
    });
    observer.observe(panelEl);

    const PANEL_TRANSITION_MS = 320;
    const id = window.setTimeout(() => observer.disconnect(), PANEL_TRANSITION_MS);
    return () => {
      window.clearTimeout(id);
      observer.disconnect();
    };
  }, [panelOpen]);

  useEffect(() => {
    return () => {
      if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current);
    };
  }, []);

  const dismissThankYou = () => {
    if (thankYouTimerRef.current) {
      clearTimeout(thankYouTimerRef.current);
      thankYouTimerRef.current = null;
    }
    setShowThankYou(false);
  };

  const showThankYouMessage = () => {
    dismissThankYou();
    setShowThankYou(true);
    thankYouTimerRef.current = setTimeout(() => {
      setShowThankYou(false);
      thankYouTimerRef.current = null;
    }, 4000);
  };

  const displayChoice = savedChoice;
  const panelChoice = panelOpen ? displayChoice || panelRating : null;

  const runFeedback = async (fn) => {
    if (disabled || !onFeedback || inFlightRef.current) return false;
    inFlightRef.current = true;
    try {
      await fn();
      return true;
    } catch {
      return false;
    } finally {
      inFlightRef.current = false;
    }
  };

  const applyRating = async (nextBool) => {
    setRatingLoading(true);
    try {
      const ok = await runFeedback(() => onFeedback(nextBool, undefined));
      if (!ok) {
        setPanelOpen(false);
      }
    } finally {
      setRatingLoading(false);
    }
  };

  const clearFeedback = async () => {
    setRatingLoading(true);
    try {
      const ok = await runFeedback(() => onFeedback(null, undefined));
      if (ok) {
        setPanelOpen(false);
        setPanelRating(null);
        setComment('');
      }
    } finally {
      setRatingLoading(false);
    }
  };

  const onUp = () => {
    if (disabled || !onFeedback) return;
    dismissThankYou();
    if (savedChoice === 'up') {
      // 再次按讚：直接取消評價（不先開啟回饋面板）
      void clearFeedback();
      return;
    }
    setPanelRating('up');
    setPanelOpen(true);
    void applyRating(true);
  };

  const onDown = () => {
    if (disabled || !onFeedback) return;
    dismissThankYou();
    if (savedChoice === 'down') {
      // 再次倒讚：直接取消評價（不先開啟回饋面板）
      void clearFeedback();
      return;
    }
    setPanelRating('down');
    setPanelOpen(true);
    void applyRating(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled || !onFeedback || savedChoice == null) return;
    const selectedLabels = [...selectedOptions].join('、');
    const trimmedComment = comment.trim();
    const finalComment = selectedLabels
      ? (trimmedComment ? `${selectedLabels}\n${trimmedComment}` : selectedLabels)
      : trimmedComment;
    setCommentLoading(true);
    try {
      const nextBool = savedChoice === 'up';
      const ok = await runFeedback(() => onFeedback(nextBool, finalComment));
      if (ok) {
        setPanelOpen(false);
        showThankYouMessage();
      }
    } finally {
      setCommentLoading(false);
    }
  };

  const isBusy = ratingLoading || commentLoading;
  const panelId = 'message-feedback-panel';
  const controlsDisabled = disabled;

  return (
    <div
      ref={feedbackScrollAnchorRef}
      className={`message-feedback-wrap${isBusy ? ' is-busy' : ''}`}
      aria-busy={isBusy}
    >
      <div className="message-feedback-row" role="group" aria-label="此則回覆評價">
        <button
          type="button"
          className={`message-feedback-btn${displayChoice === 'up' ? ' is-active is-up' : ''}${ratingLoading ? ' is-loading' : ''}`}
          onClick={onUp}
          aria-pressed={displayChoice === 'up'}
          aria-expanded={panelOpen && panelChoice === 'up'}
          aria-controls={panelOpen ? panelId : undefined}
          aria-label="按讚：有幫助"
          title="有幫助"
          disabled={controlsDisabled}
        >
          <svg
            className="message-feedback-icon"
            fill="currentColor"
            width={20}
            height={20}
            viewBox="3 3 18 18"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M11.5625 5.1582C12.6978 5.15833 13.5696 6.16421 13.4092 7.28809L13.1934 8.80176C13.1398 9.17636 13.4302 9.51156 13.8086 9.51172H16.7285C18.4459 9.51182 19.8379 10.9046 19.8379 12.6221V13.2051C19.8378 13.7461 19.6962 14.2783 19.4277 14.748L17.627 17.9014C17.2947 18.4827 16.6754 18.8418 16.0059 18.8418H7.39941V18.8408H6.02832C4.99794 18.8408 4.16229 18.0059 4.16211 16.9756V11.3779C4.16211 10.3474 4.99782 9.51172 6.02832 9.51172H7.67773C7.7177 9.42357 7.76034 9.33641 7.80859 9.25195L9.61035 6.09863C9.94249 5.5174 10.5611 5.15836 11.2305 5.1582H11.5625ZM6.02832 10.7559C5.68482 10.7559 5.40625 11.0344 5.40625 11.3779V16.9756C5.40643 17.3189 5.68493 17.5977 6.02832 17.5977H7.39844V10.7559H6.02832ZM11.2305 6.40234C11.0074 6.4025 10.8011 6.52216 10.6904 6.71582L8.88867 9.86914C8.72766 10.151 8.64258 10.4703 8.64258 10.7949V17.5977H16.0059C16.229 17.5977 16.4361 17.4779 16.5469 17.2842L18.3486 14.1309C18.5097 13.849 18.5937 13.5297 18.5938 13.2051V12.6221C18.5937 11.5916 17.7589 10.756 16.7285 10.7559H13.8086C12.6733 10.7557 11.8015 9.74984 11.9619 8.62598L12.1777 7.1123C12.2313 6.73769 11.9409 6.40247 11.5625 6.40234H11.2305Z" />
          </svg>
        </button>
        <button
          type="button"
          className={`message-feedback-btn${displayChoice === 'down' ? ' is-active is-down' : ''}${ratingLoading ? ' is-loading' : ''}`}
          onClick={onDown}
          aria-pressed={displayChoice === 'down'}
          aria-expanded={panelOpen && panelChoice === 'down'}
          aria-controls={panelOpen ? panelId : undefined}
          aria-label="倒讚：沒幫助"
          title="沒幫助"
          disabled={controlsDisabled}
        >
          <svg
            className="message-feedback-icon"
            fill="currentColor"
            width={20}
            height={20}
            viewBox="3 3 18 18"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              transform="translate(0 2)"
              d="M12.4375 18.8418C11.3022 18.8417 10.4304 17.8358 10.5908 16.7119L10.8066 15.1982C10.8602 14.8236 10.5698 14.4884 10.1914 14.4883L7.27148 14.4883C5.55407 14.4882 4.16211 13.0954 4.16211 11.3779L4.16211 10.7949C4.16216 10.2539 4.30385 9.72173 4.57227 9.25195L6.37305 6.09863C6.70526 5.51731 7.32458 5.1582 7.99414 5.1582L16.6006 5.1582L16.6006 5.15918L17.9717 5.15918C19.0021 5.15918 19.8377 5.99407 19.8379 7.02441L19.8379 12.6221C19.8379 13.6526 19.0022 14.4883 17.9717 14.4883L16.3223 14.4883C16.2823 14.5764 16.2397 14.6636 16.1914 14.748L14.3896 17.9014C14.0575 18.4826 13.4389 18.8416 12.7695 18.8418L12.4375 18.8418ZM17.9717 13.2441C18.3152 13.2441 18.5938 12.9656 18.5938 12.6221L18.5938 7.02441C18.5936 6.68107 18.3151 6.40234 17.9717 6.40234L16.6016 6.40234L16.6016 13.2441L17.9717 13.2441ZM12.7695 17.5977C12.9926 17.5975 13.1989 17.4778 13.3096 17.2842L15.1113 14.1309C15.2723 13.849 15.3574 13.5297 15.3574 13.2051L15.3574 6.40234L7.99414 6.40234C7.77097 6.40234 7.56387 6.52207 7.45313 6.71582L5.65137 9.86914C5.49035 10.151 5.4063 10.4703 5.40625 10.7949L5.40625 11.3779C5.40625 12.4084 6.24107 13.244 7.27148 13.2441L10.1914 13.2441C11.3267 13.2443 12.1985 14.2502 12.0381 15.374L11.8223 16.8877C11.7687 17.2623 12.0591 17.5975 12.4375 17.5977L12.7695 17.5977Z"
            />
          </svg>
        </button>
      </div>

      <div
        id={panelId}
        ref={feedbackPanelRef}
        className={`message-feedback-panel${panelOpen ? ' is-open' : ''}`}
        aria-hidden={!panelOpen}
      >
        <form className="message-feedback-form" onSubmit={onSubmit}>
          <label className="message-feedback-label" htmlFor={`${panelId}-input`}>
            {panelChoice === 'up' ? '告訴我們哪裡有幫助（選填）' : '告訴我們哪裡需要改進（選填）'}
          </label>
          {panelChoice === 'down' && quickOptions.length > 0 && (
            <div className="feedback-quick-options" role="group" aria-label="快速選項">
              {quickOptions.map((opt) => (
                <button
                  key={opt.param_name}
                  type="button"
                  className={`feedback-quick-chip${selectedOptions.has(opt.param_name) ? ' is-selected' : ''}`}
                  onClick={() => {
                    setSelectedOptions(prev => {
                      const next = new Set(prev);
                      if (next.has(opt.param_name)) {
                        next.delete(opt.param_name);
                      } else {
                        next.add(opt.param_name);
                      }
                      return next;
                    });
                  }}
                  tabIndex={panelOpen ? 0 : -1}
                  aria-pressed={selectedOptions.has(opt.param_name)}
                  disabled={controlsDisabled}
                >
                  {opt.param_name}
                </button>
              ))}
            </div>
          )}
          <textarea
            id={`${panelId}-input`}
            className="message-feedback-input"
            rows={3}
            maxLength={2000}
            placeholder="輸入您的意見…"
            value={comment}
            onChange={(ev) => setComment(ev.target.value)}
            readOnly={!panelOpen}
            tabIndex={panelOpen ? 0 : -1}
          />
          <div className="message-feedback-actions">
            <button
              type="button"
              className="message-feedback-cancel"
              onClick={() => {
                setPanelOpen(false);
                setPanelRating(null);
                setComment(userComment || '');
              }}
              disabled={controlsDisabled}
            >
              取消
            </button>
            <button
              type="submit"
              className={`message-feedback-submit${commentLoading ? ' is-loading' : ''}`}
              disabled={controlsDisabled || !panelOpen}
            >
              {commentLoading ? '送出中…' : '送出'}
            </button>
          </div>
        </form>
      </div>

      {showThankYou && (
        <p className="message-feedback-thankyou" role="status" aria-live="polite">
          感謝您協助提升 MyBro 品質
        </p>
      )}
    </div>
  );
}

/**
 * 圖片 Lightbox：點擊圖附件後在原頁面以全螢幕遮罩顯示原圖。
 * ESC 或點擊背景關閉。
 */
function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="圖片預覽">
      <button className="lightbox-close" onClick={onClose} aria-label="關閉">✕</button>
      <img
        className="lightbox-img"
        src={src}
        alt="預覽圖片"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/**
 * 點擊附件時以 fetch + JWT 下載，避免瀏覽器原生跳轉無法帶 Authorization header。
 * 僅在 att.serverUrl（BFF proxy 路徑）存在時攔截；blob URL 直接放行。
 */
async function handleAttachmentClick(e, att) {
  if (!att.serverUrl) return; // blob URL fallback，讓 <a href> 正常開啟
  e.preventDefault();

  const token = appStorage.getItemSync('mygenie_token');
  if (!token) {
    // 未登入：直接嘗試 previewUrl（blob），至少讓圖片可預覽
    if (att.previewUrl) window.open(att.previewUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    const response = await fetch(att.serverUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`status ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    // 圖片：新分頁開啟；文件：直接下載
    if (att.type?.startsWith('image/')) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    } else {
      anchor.download = att.name || 'download';
    }
    anchor.click();
    // 短暫延遲後釋放 blob URL 記憶體
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    console.error('Attachment download error:', err);
    // 降級：嘗試以 previewUrl 開啟（圖片仍可預覽）
    if (att.previewUrl) window.open(att.previewUrl, '_blank', 'noopener,noreferrer');
  }
}

/**
 * 圖片縮圖元件：優先顯示 previewUrl（blob），失效時帶 JWT 向 serverUrl 拉取並重建。
 * 從 null 開始不渲染，確認 URL 有效後才顯示，徹底避免破圖閃爍。
 * _imagePromises cache 確保同一 URL 整個 session 只發一次 HTTP request。
 */
function AttachmentImage({ previewUrl, serverUrl, alt, className, onLoad }) {
  const [src, setSrc] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 先用隱藏 Image 測試 previewUrl 是否仍有效（blob URL 只在當次 session 有效）
      if (previewUrl) {
        const valid = await new Promise(resolve => {
          const probe = new Image();
          probe.onload = () => resolve(true);
          probe.onerror = () => resolve(false);
          probe.src = previewUrl;
        });
        if (cancelled) return;
        if (valid) { setSrc(previewUrl); return; }
      }

      // previewUrl 失效或不存在，改從 server 下載
      if (!serverUrl) return;
      const blob = await fetchImageBlobCached(serverUrl);
      if (cancelled || !blob) return;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      if (!cancelled) setSrc(url);
    })();

    return () => { cancelled = true; };
  }, [previewUrl, serverUrl]);

  if (!src) return null;
  return <img src={src} alt={alt} className={className} onLoad={onLoad} />;
}

// mcpAppsInteractive／onMcpAppSendMessage（AGUI-1274）：見下方 MCP Apps 互動畫面區塊
const ChatMessage = forwardRef(({ message, chatId, onMessageFeedback, feedbackDisabled = false, feedbackQuickOptions = [], onImageLoad, onResend, mcpAppsInteractive = false, onMcpAppSendMessage }, ref) => {

  const isUser = message.sender === 'guest';
  const { fileUploadEnabled } = useAppConfig();

  const [lightboxSrc, setLightboxSrc] = useState(null);
  const lightboxBlobRef = useRef(null);

  /** A2UI 執行期失敗時改走圖表／HTML／Markdown；訊息或正文變更時重試 A2UI。 */
  const [a2uiFallbackToRich, setA2uiFallbackToRich] = useState(false);
  const onA2uiFailed = useCallback(() => setA2uiFallbackToRich(true), []);

  // unmount 時釋放 lightbox 專用的 blob URL
  useEffect(() => {
    return () => {
      if (lightboxBlobRef.current) URL.revokeObjectURL(lightboxBlobRef.current);
    };
  }, []);

  const closeLightbox = useCallback(() => {
    if (lightboxBlobRef.current) {
      URL.revokeObjectURL(lightboxBlobRef.current);
      lightboxBlobRef.current = null;
    }
    setLightboxSrc(null);
  }, []);

  const handleImageClick = useCallback(async (e, att) => {
    e.preventDefault();
    if (!att.serverUrl) {
      if (att.previewUrl) setLightboxSrc(att.previewUrl);
      return;
    }
    const token = appStorage.getItemSync('mygenie_token');
    if (!token) {
      if (att.previewUrl) setLightboxSrc(att.previewUrl);
      return;
    }
    try {
      const res = await fetch(att.serverUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const blob = await res.blob();
      if (lightboxBlobRef.current) URL.revokeObjectURL(lightboxBlobRef.current);
      const url = URL.createObjectURL(blob);
      lightboxBlobRef.current = url;
      setLightboxSrc(url);
    } catch {
      if (att.previewUrl) setLightboxSrc(att.previewUrl);
    }
  }, []);

  // Theme-aware syntax highlighting: Prism themes inject inline styles.
  // We track DOM theme changes so code blocks update immediately.
  const [isLightTheme, setIsLightTheme] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement?.getAttribute('data-theme') === 'light';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => setIsLightTheme(root.getAttribute('data-theme') === 'light');
    update();
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'data-theme') {
          update();
          break;
        }
      }
    });
    mo.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);

  const syntaxTheme = useMemo(() => (isLightTheme ? oneLight : tomorrow), [isLightTheme]);
  const syntaxCustomStyle = useMemo(
    () => ({
      background: 'var(--md-pre-bg)',
      color: 'var(--text)',
      border: '1px solid var(--md-pre-border)',
      borderRadius: 8,
      padding: 16,
      margin: '16px 0',
      overflowX: 'auto'
    }),
    []
  );
  const syntaxCodeTagProps = useMemo(
    () => ({
      style: {
        background: 'transparent',
        color: 'inherit'
      }
    }),
    []
  );

  // 確保 message.text 是字符串（須在任何 hook 之前計算，供後續 hook 依賴；notice 仍可提早 return JSX）
  const messageText =
    typeof message.text === 'string' ? message.text : (message.text?.text || String(message.text || ''));

  const onA2UIClientAction = useCallback((evt) => {
    console.info('[A2UI] client→server', evt);
  }, []);

  /**
   * 機器人回覆：先用 isA2uiV08BotReplyPayload 判定是否為 A2UI v0.8；
   * 為真再以 parse + normalize（並排除元件循環）產出給 Renderer 的 payload。
   * 為假則不產 payload，交由下方圖表／HTML／Markdown 鏈。
   */
  const botPlainFirstA2uiPayload = useMemo(() => {
    if (isUser || message.sender === 'notice') return null;
    if (!isA2uiV08BotReplyPayload(messageText)) return null;
    const parsed = parseA2uiV08MessagesFromPayload(messageText);
    if (!parsed) return null;
    const messages = normalizeA2uiV08Messages(parsed.messages);
    if (hasA2uiV08ComponentCycle(messages)) return null;
    return { surfaceId: parsed.surfaceId, messages };
  }, [isUser, message.sender, messageText]);

  useEffect(() => {
    setA2uiFallbackToRich(false);
  }, [message.id, messageText]);

  // MCP Apps 互動畫面（AGUI-1274）：訊息上的 mcpApps 已於落地時正規化並依 toolCallId 去重。
  // 串流中的暫時泡泡不顯示（正式訊息落地後才有畫面）。
  const mcpApps = !isUser && !message.isStreaming && Array.isArray(message.mcpApps) ? message.mcpApps : null;
  const hasMcpApps = !!mcpApps && mcpApps.length > 0;
  // HTML 經後端讀取，apiService 以 server+uri 做記憶體快取（不落地 IndexedDB）
  const loadMcpAppResource = useCallback(
    (marker) => apiService.getMcpAppResource(chatId, marker.server, marker.resourceUri),
    [chatId]
  );
  // 畫面要求以使用者身分送出（ui/message）：交給 App 層；沒有提供時一律回「沒送出」
  const sendFromMcpApp = useCallback(
    (text) => (onMcpAppSendMessage ? onMcpAppSendMessage(text) : Promise.resolve(false)),
    [onMcpAppSendMessage]
  );
  
  // 停止/通知類訊息（sender: 'notice'），置中顯示，不使用氣泡樣式
  // 注意：sender: 'system'（例如忙線提示）走 chatbot 氣泡路徑
  if (message.sender === 'notice') {
    return (
      <div ref={ref} className="system-msg">
        <div className="system-msg-inner">
          <span>{message.text}</span>
          <MessageTime message={message} />
        </div>
      </div>
    );
  }

  // 檢測並解析圖表數據
  const chartData = parseChartData(messageText);

  // 尚未收到任何文字時顯示 loading 動畫；串流中一旦有部分文字（message.isStreaming），
  // 即使 message.isLoading 仍為 true 也改為往下走一般渲染路徑，即時顯示已累積的文字
  // 只有互動畫面、沒有文字的回覆不可當成「載入中」顯示打字動畫
  if (!isUser && !message.hasChart && !messageText && !hasMcpApps) {
    return (
      <div ref={ref} className="chatbot-msg">
        <div className="message-content">
          <div className="markdown-body">
            <div className="typing-indicator" aria-live="polite" aria-label="Loading">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} className={isUser ? 'guest-msg' : 'chatbot-msg'}>
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={closeLightbox} />}
      {isUser ? (
        <div className="guest-message-column">
          <div className="message-content">
            {fileUploadEnabled && message.attachments?.length > 0 && (
              <div className="msg-attachments">
                {message.attachments.map((att, idx) => {
                  const isImage = att.type?.startsWith('image/');
                  return (
                    <div
                      key={idx}
                      className={`msg-attachment-item${isImage ? ' msg-attachment-item--image' : ''}`}
                      title={att.name}
                      style={{ cursor: 'default', pointerEvents: 'none' }}
                    >
                      {isImage ? (
                        <AttachmentImage previewUrl={att.previewUrl} serverUrl={att.serverUrl} alt={att.name} className="msg-attachment-img" onLoad={onImageLoad} />
                      ) : (
                        <div className="msg-attachment-file">
                          <span className="msg-attachment-icon" aria-hidden="true">📄</span>
                          <span className="msg-attachment-name">{att.name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {messageText && <span>{messageText}</span>}
          </div>
          <MessageTime message={message} />
          {/* 終局送達失敗提示（change: unify-reply-completion-signal Section 5）：deliveryFailed
              由 useChat.js 依 pendingReply.isDeliveryFailed 標記，代表補收輪詢已判定終局時限
              到期、後端仍未確認收到這則提問。三條鐵律之一「一定要給出口」——不能只是無聲繼續
              等待，必須明確告知並提供重新發送的路徑（見 project-send-never-arrived-ux）。 */}
          {message.deliveryFailed && (
            <div className="guest-msg-delivery-failed">
              <span>⚠️ 這則訊息可能未成功送達</span>
              {onResend && (
                <button
                  type="button"
                  className="guest-msg-resend-button"
                  onClick={() => onResend(message)}
                >
                  重新發送
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bot-message-block">
          <div className="message-content">
            <div className="markdown-body">
            {/* A2UI 成功前置條件通過且不曾在執行期失敗時畫 surface；任一錯誤則改用圖表／HTML／Markdown。 */}
            {botPlainFirstA2uiPayload && !a2uiFallbackToRich ? (
              <A2uiRenderErrorBoundary
                fallback={null}
                onError={onA2uiFailed}
              >
                <div className="bot-reply-plain-first bot-reply-a2ui-first">
                  <A2UIReplySurface
                    surfaceId={botPlainFirstA2uiPayload.surfaceId}
                    messages={botPlainFirstA2uiPayload.messages}
                    onClientAction={onA2UIClientAction}
                    onProcessMessagesError={onA2uiFailed}
                  />
                </div>
              </A2uiRenderErrorBoundary>
            ) : (message.hasChart && message.chartData ? (
              <div>
                {console.log('ChatMessage - 圖表標題:', message.chartTitle)}
                <ChatChart 
                  data={message.chartData} 
                  title={message.chartTitle || `圖表數據 - ${new Date().toLocaleTimeString()}`}
                  question={messageText}
                  chartType={message.chartType}
                  xlabel={message.xlabel}
                  ylabel={message.ylabel}
                />
                <div style={{ marginTop: '16px' }}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex, rehypeRaw, rehypeSanitize]}
                    components={{
                      a({ node, href, children, ...props }) {
                        // 確保所有外部連結在新分頁打開
                        return (
                          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {children}
                          </a>
                        );
                      },
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : '';

                        return !inline && language ? (
                          <div className="codeblock-wrapper">
                            <button
                              className="copy-icon-btn"
                              title="複製程式碼"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const raw = String(children).replace(/\n$/, '');
                                if (navigator?.clipboard?.writeText) {
                                  navigator.clipboard.writeText(raw);
                                }
                              }}
                            >📋</button>
                            <SyntaxHighlighter
                              style={syntaxTheme}
                              customStyle={syntaxCustomStyle}
                              codeTagProps={syntaxCodeTagProps}
                              language={language}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          </div>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {messageText}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (typeof messageText === 'string' && !messageText.includes('```') && (messageText.includes('<div') || messageText.includes('<span') || messageText.includes('<button') || messageText.includes('<canvas') || messageText.includes('<script') || messageText.includes('<html') || messageText.includes('<body'))) ? (
              <div className="html-render-options">
                
                <SafeHTMLRenderer 
                  htmlContent={messageText} 
                  title={`Safe HTML - ${new Date().toLocaleTimeString()}`}
                />
              </div>
            ) : (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex, rehypeRaw, rehypeSanitize]}
                components={{
                  a({ node, href, children, ...props }) {
                    // 確保所有外部連結在新分頁打開
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                      </a>
                    );
                  },
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';

                    return !inline && language ? (
                      <div className="codeblock-wrapper">
                        <button
                          className="copy-icon-btn"
                          title="複製程式碼"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const raw = String(children).replace(/\n$/, '');
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard.writeText(raw);
                            }
                          }}
                        >📋</button>
                        <SyntaxHighlighter
                          style={syntaxTheme}
                          customStyle={syntaxCustomStyle}
                          codeTagProps={syntaxCodeTagProps}
                          language={language}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                
                {messageText}
              </ReactMarkdown>
            ))}
          </div>
          {/* 工具執行中（後端已發 AI_STREAM_CLEAR、清空後的新內容尚未送達）：
              在已顯示的過程文字下方補上打字指示，讓使用者知道仍在處理中，
              而不是停在一段看起來已經講完的文字上（見 useChat.js 的 isToolProcessing） */}
          {message.isProcessing && (
            <div className="typing-indicator typing-indicator-inline" aria-live="polite" aria-label="處理中">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          )}
          </div>
          {/* MCP Apps 互動畫面（AGUI-1274，例如簡報建立畫面）：顯示在回覆內容下方、時間與評價之上。
              可否操作（interactive）由 App 依「此訊息之後是否已有使用者訊息」決定 */}
          {hasMcpApps && (
            <div className="mcp-app-list">
              {mcpApps.map((app) => (
                <McpAppFrame
                  key={`mcp-app:${app.toolCallId || `${app.server}|${app.resourceUri}`}`}
                  marker={app}
                  interactive={mcpAppsInteractive}
                  title={mcpAppTitle(app.toolName)}
                  loadResource={loadMcpAppResource}
                  onSendMessage={sendFromMcpApp}
                />
              ))}
            </div>
          )}
          {!message.isStreaming && <MessageTime message={message} />}
          {!message.isStreaming && (
            <BotMessageFeedback
              userFeedback={message.userFeedback}
              userComment={message.userComment}
              onFeedback={(next, comment) =>
                onMessageFeedback && chatId && message.id
                  ? onMessageFeedback(chatId, message.id, next, comment)
                  : Promise.resolve()
              }
              disabled={feedbackDisabled || !onMessageFeedback || !chatId || !message.id}
              quickOptions={feedbackQuickOptions}
            />
          )}
        </div>
      )}
    </div>
  );
});

export default ChatMessage;
